import * as React from "react";

import { truncateText } from "@/lib/truncate-text";
import { cn } from "@/lib/utils";

// The changed-from-base highlight's pair — #1043's variant E, "old beside new"
// (ledger §7, "Changed from base widens"; owner rulings D1–D6): the base value
// struck in grey, a thin grey arrow, then the current value drawn exactly as it
// is drawn without the switch. An Atlas addition with no donor: modsy marks a
// changed row with its tint and info icon only.
//
//   - **the old side** — a `<del>`: `--color-mui-grey-700` ink and strike,
//     0.875em, weight 400 (grey 700, not the brief's 600, for AA contrast on
//     the changed tint and the group header's grey: owner disposition D6 = b
//     on PR #1106). A visible cut keeps the whole text in `title` and in
//     the announcement (owner disposition b on PR #1061: the announced old value
//     is always the full, uncut text);
//   - **the arrow** — an inline SVG stroke in the same grey, 1.25px with round
//     caps, hidden from assistive technology: a straight → between the two on
//     one line, or a two-part elbow ↳ from the old line down into the new;
//   - **the new side** — an `<ins>` with no decoration around the value as the
//     caller draws it.
//
// An empty side reads *None*. A reader hears "Base value: X. Now: Y" — or,
// inside a `PairWording` (#1090), the push preview's "Now: X. After the push:
// Y" or its history's "Before: X. After: Y": the same pair, the old side being
// what the community has now (had) and the new what it gets (got).
//
// **The measured arrow.** In its default layout the pair is laid out inline,
// and before paint it reads the old and new values' line boxes
// (`getClientRects`): it keeps the → only when the old value occupies one line
// box and the new value's first box shares it, and otherwise stacks with the
// ↳ (narrow columns stack rather than restyle, D5). It measures again from
// inline when its container's width changes — width only, and never for a
// width its own choice caused, since a table cell narrows when its pair stacks
// and would otherwise flip it back and forth — when the web font arrives, and
// when an enclosing height transition (a group's expand) ends. The `block` layout is
// always stacked, for values that never sit on one line: notes, an item name,
// a maker, a locations line.

/** Which pair a reader hears: against the base, a push's preview, or its history. */
export type PairWordingValue = "base" | "push" | "history";

const PairWordingContext = React.createContext<PairWordingValue>("base");

/** Words every pair and ghost picture inside it as a push's (#1090). */
export function PairWording({
  value,
  children,
}: {
  value: PairWordingValue;
  children: React.ReactNode;
}) {
  return <PairWordingContext value={value}>{children}</PairWordingContext>;
}

/** The announcement's two lead-ins, for a value and for a picture. */
const SPOKEN: Record<
  PairWordingValue,
  { old: string; now: string; oldPicture: string; nowPicture: string }
> = {
  base: { old: "Base value", now: "Now", oldPicture: "Base item picture", nowPicture: "Now" },
  push: {
    old: "Now",
    now: "After the push",
    oldPicture: "Picture now",
    nowPicture: "After the push",
  },
  history: { old: "Before", now: "After", oldPicture: "Picture before", nowPicture: "After" },
};

/**
 * What a ghost picture is announced by, in its place: the old picture's name,
 * then the new picture's lead-in — "Base item picture: X. Now: ".
 */
export function SpokenOldPicture({ name }: { name: string }) {
  const spoken = SPOKEN[React.use(PairWordingContext)];
  return <span className="sr-only">{`${spoken.oldPicture}: ${name}. ${spoken.nowPicture}: `}</span>;
}

/** The pair's grey: the old side's ink and the arrow's stroke. */
const BASE_INK = "text-mui-grey-700";

const ARROW_STROKE = {
  "aria-hidden": true,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.25,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

/** The straight →, on the old value's line — and in the push preview's legend. */
export function RightArrow() {
  return (
    <svg
      {...ARROW_STROKE}
      viewBox="0 0 14 10"
      className={cn(BASE_INK, "mx-1 inline-block h-[0.6em] w-[0.85em] shrink-0 align-middle")}
    >
      <path d="M1 5h11M8.5 1.5 12 5 8.5 8.5" />
    </svg>
  );
}

/** The elbow ↳, from the old value's line down into the new one. */
function ElbowArrow() {
  return (
    <svg
      {...ARROW_STROKE}
      viewBox="0 0 12 12"
      className={cn(BASE_INK, "mt-[0.2em] size-[0.75em] shrink-0")}
    >
      <path d="M2 1v5.5a1.5 1.5 0 0 0 1.5 1.5H10M7 5l3 3-3 3" />
    </svg>
  );
}

/** The vertical ↓ between two stacked pictures. */
export function DownArrow() {
  return (
    <svg {...ARROW_STROKE} viewBox="0 0 10 14" className={cn(BASE_INK, "h-3.5 w-2.5 shrink-0")}>
      <path d="M5 1v11M1.5 8.5 5 12l3.5-3.5" />
    </svg>
  );
}

/** *None*: an empty side. */
function NoneText() {
  return <span className="text-[0.875em] italic">None</span>;
}

/** Whether the caller's new value draws nothing — it then reads *None*. */
function isEmptyValue(value: React.ReactNode) {
  return value === null || value === undefined || value === false || value === "";
}

/** The old side: struck, grey, announced whole whatever the eye sees. */
function OldSide({
  old,
  cutAt,
  alone = false,
  className,
}: {
  old: string | null;
  cutAt?: number;
  /** Nothing is beside it: the announcement says the new side is None. */
  alone?: boolean;
  className?: string;
}) {
  const spoken = SPOKEN[React.use(PairWordingContext)];
  const cut = old !== null && cutAt !== undefined ? truncateText(old, cutAt) : old;
  return (
    <del
      title={old !== null && cut !== old ? old : undefined}
      className={cn(
        BASE_INK,
        "decoration-mui-grey-700 text-[0.875em] font-normal line-through",
        className,
      )}
    >
      <span className="sr-only">
        {`${spoken.old}: ${old ?? "None"}. ${spoken.now}: ${alone ? "None" : ""}`}
      </span>
      <span aria-hidden="true">{old === null ? <NoneText /> : cut}</span>
    </del>
  );
}

/** The new side: the value as drawn without the switch, undecorated. */
function NewSide({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <ins className={cn("no-underline", className)}>
      {isEmptyValue(children) ? <NoneText /> : children}
    </ins>
  );
}

export interface OldBesideNewProps {
  /** The base value's whole text; `null` for an empty side, which reads *None*. */
  old: string | null;
  /**
   * Cut a long old value for the eye at this many characters, `...` after
   * (`truncateText`); the whole text stays in `title` and the announcement.
   */
  cutAt?: number;
  /** `measured` (the default) chooses → or ↳ by the rendered lines; `block` always stacks. */
  layout?: "measured" | "block";
  /** The current value, drawn as without the switch; nothing reads *None*. */
  children: React.ReactNode;
  className?: string;
}

export function OldBesideNew({ layout = "measured", ...props }: OldBesideNewProps) {
  return layout === "block" ? <StackedPair {...props} /> : <MeasuredPair {...props} />;
}

function StackedPair({
  old,
  cutAt,
  children,
  className,
  ref,
  measured = false,
}: Omit<OldBesideNewProps, "layout"> & {
  ref?: React.Ref<HTMLSpanElement>;
  /** The measured pair's stack, as against the block form. */
  measured?: boolean;
}) {
  // The block form fills its container, so a value that fills it without
  // the pair still does (the notes' row, its More / Less at the right); the
  // measured pair's stack stays as wide as its text, inline in its line.
  const block = !measured;
  return (
    <span
      ref={ref}
      data-old-beside-new={measured ? "stacked" : "block"}
      className={cn(
        "max-w-full flex-col items-start gap-0.5 align-top",
        block ? "flex w-full" : "inline-flex",
        className,
      )}
    >
      <OldSide old={old} cutAt={cutAt} />
      <span className={cn("flex max-w-full min-w-0 items-start gap-1", block && "w-full")}>
        <ElbowArrow />
        <NewSide className={cn("min-w-0", block && "grow")}>{children}</NewSide>
      </span>
    </span>
  );
}

type MeasureMode = "measure" | "inline" | "stacked";

function MeasuredPair({ old, cutAt, children, className }: Omit<OldBesideNewProps, "layout">) {
  const boxRef = React.useRef<HTMLSpanElement>(null);
  const oldRef = React.useRef<HTMLSpanElement>(null);
  const newRef = React.useRef<HTMLSpanElement>(null);
  const [mode, setMode] = React.useState<MeasureMode>("measure");
  /** The container's width when the current choice was made. */
  const decidedWidth = React.useRef<number | null>(null);

  React.useLayoutEffect(() => {
    if (mode === "measure") {
      if (!oldRef.current || !newRef.current) return;
      const oldBoxes = [...oldRef.current.getClientRects()].filter((box) => box.width > 0);
      const newBoxes = [...newRef.current.getClientRects()].filter((box) => box.width > 0);
      const lastOld = oldBoxes.at(-1);
      const firstNew = newBoxes[0];
      const oldLines = new Set(oldBoxes.map((box) => Math.round(box.top))).size;
      const sameLine =
        oldLines === 1 &&
        lastOld !== undefined &&
        firstNew !== undefined &&
        firstNew.top < lastOld.bottom &&
        firstNew.bottom > lastOld.top;
      setMode(sameLine ? "inline" : "stacked");
      return;
    }
    // The choice is committed: the width it leaves the container at is the
    // one later width changes are compared against.
    decidedWidth.current = boxRef.current?.parentElement?.clientWidth ?? null;
  }, [mode]);

  React.useEffect(() => {
    // The container outlives the pair's two shapes; the pair's own box does
    // not (each mode renders its own), so it is read afresh where needed.
    const host = boxRef.current?.parentElement;
    if (!host) return;
    let cancelled = false;
    const again = () => {
      if (!cancelled) setMode("measure");
    };
    const observer = new ResizeObserver(() => {
      if (decidedWidth.current === null || host.clientWidth === decidedWidth.current) return;
      again();
    });
    observer.observe(host);
    void document.fonts.ready.then(again);
    // An enclosing group's expand: its collapse's height transition. Any
    // other transition (a row's hover) changes no line box.
    const onTransitionEnd = (event: TransitionEvent) => {
      const box = boxRef.current;
      if (
        event.propertyName === "height" &&
        box &&
        event.target instanceof Node &&
        event.target !== box &&
        event.target.contains(box)
      ) {
        again();
      }
    };
    document.addEventListener("transitionend", onTransitionEnd, true);
    return () => {
      cancelled = true;
      observer.disconnect();
      document.removeEventListener("transitionend", onTransitionEnd, true);
    };
  }, []);

  if (mode === "stacked") {
    return (
      <StackedPair ref={boxRef} measured old={old} cutAt={cutAt} className={className}>
        {children}
      </StackedPair>
    );
  }
  return (
    <span ref={boxRef} data-old-beside-new={mode} className={className}>
      <span ref={oldRef}>
        <OldSide old={old} cutAt={cutAt} />
      </span>
      <RightArrow />
      {/* Measured on its own, not with the arrow: the arrow can keep the old
          value's line while the value wraps to the next. */}
      <span ref={newRef}>
        <NewSide>{children}</NewSide>
      </span>
    </span>
  );
}

/**
 * An old value with nothing beside it — a cleared paid-upgrade mark: the
 * struck text alone, no arrow, announced "Base value: X. Now: None" (or the
 * `PairWording`'s words).
 */
export function OldValueAlone({ children }: { children: string }) {
  return <OldSide old={children} alone className="block" />;
}

/**
 * Text struck in the pair's ink, with no arrow and no announcement of its own:
 * a removed room's name over its old notes (#1106 D8), so it follows the
 * pair's grey wherever that is set.
 */
export function StruckText({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <del className={cn(BASE_INK, "line-through decoration-current", className)}>{children}</del>
  );
}
